import AddToCartButton from "~/components/product/AddToCartButton"
import Image from "~/components/ui/Image"
import type { ToAddToCart } from "~/sdk/ga4/events/add_to_cart"
import { ImageWidget } from "~/types/widgets"
import type React from "react"
import { useEffect, useState } from "react"

interface Item { id: string; seller: string; quantity: number }

export interface Props {
    product: Item
    productName: string
    imageUrl?: string
    productGroupId: string
    listPrice: string | null
    finalPrice: string | null
    price: string | null
    discountLabel?: string
    paymentMethod?: string
    installments?: string
    inventoryLevel: number
    hasVariants: boolean
    analytics?: ToAddToCart
    disabled?: boolean
    icon?: ImageWidget
    iconWidth?: number
    iconHeight?: number
}

export default function StickyBuyBox({
    product,
    productName,
    imageUrl,
    productGroupId,
    listPrice,
    finalPrice,
    price,
    discountLabel,
    paymentMethod,
    installments,
    inventoryLevel,
    hasVariants,
    analytics,
    disabled,
    icon,
    iconWidth,
    iconHeight,
}: Props) {
    // useState and not the `useSignal` shim: `visible` is written from a scroll
    // listener and read during render, which is exactly the shape the React
    // Compiler memoizes into a frozen read (DECO-5817).
    const [visible, setVisible] = useState(false)
    const [quantity, setQuantity] = useState(1)
    // What the user is currently typing. `null` = not typing, show `quantity`.
    // Same reason as AddToCartWithQuantitySelector: the origin's input is
    // `value=` + `onBlur` only, which React treats as read-only and refuses to
    // let the user type into (DECO-5823).
    const [draft, setDraft] = useState<string | null>(null)

    useEffect(() => {
        const buyBox = document.getElementById('product-buy-box-anchor')
        if (!buyBox) return

        const updateVisibility = () => {
            setVisible(buyBox.getBoundingClientRect().bottom <= 0)
        }

        const observer = new IntersectionObserver(updateVisibility, {
            threshold: [0, 1],
        })

        observer.observe(buyBox)
        globalThis.addEventListener('scroll', updateVisibility, { passive: true })
        globalThis.addEventListener('resize', updateVisibility)
        updateVisibility()

        return () => {
            observer.disconnect()
            globalThis.removeEventListener('scroll', updateVisibility)
            globalThis.removeEventListener('resize', updateVisibility)
        }
    }, [])

    const clamp = (value: number) =>
        Number.isFinite(value) ? Math.max(1, Math.min(inventoryLevel, value)) : 1

    const decrement = () => {
        setDraft(null)
        setQuantity((q) => Math.max(1, q - 1))
    }

    const increment = () => {
        setDraft(null)
        setQuantity((q) => Math.min(inventoryLevel, q + 1))
    }

    const typeQuantity = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value, valueAsNumber } = event.currentTarget
        setDraft(value)
        if (value !== '') setQuantity(clamp(valueAsNumber))
    }

    const changeQuantity = (event: React.FocusEvent<HTMLInputElement>) => {
        setDraft(null)
        setQuantity(clamp(event.currentTarget.valueAsNumber))
    }

    return (
        <aside
            className={`fixed inset-x-0 bottom-0 z-40 bg-white shadow-[0_-4px_16px_rgba(0,0,0,0.12)] transition-[transform,opacity] duration-300 ${
                visible
                    ? 'translate-y-0 opacity-100'
                    : 'pointer-events-none translate-y-full opacity-0'
            }`}
            aria-hidden={!visible}
            aria-label='Compra rápida'
        >
            <div className='mx-auto flex min-h-[184px] max-w-[1240px] items-center gap-2 px-2 pb-[max(16px,env(safe-area-inset-bottom))] pt-2 lg:min-h-[88px] lg:gap-10 lg:px-6 lg:py-2'>
                <div className='flex min-h-40 min-w-0 flex-1 items-center gap-2 lg:h-auto lg:min-h-[72px] lg:max-w-[425px]'>
                    {imageUrl && (
                        <img
                            src={imageUrl}
                            alt=''
                            width={160}
                            height={160}
                            loading='lazy'
                            className='h-40 w-[min(40vw,160px)] shrink-0 self-start rounded object-contain lg:h-[72px] lg:w-[72px] lg:self-center'
                        />
                    )}

                    <div className='flex min-h-40 min-w-0 flex-1 flex-col gap-2 overflow-visible lg:h-auto lg:min-h-0 lg:justify-center lg:gap-1.5'>
                        {/* Konfidency injeta filhos aqui antes da hidratação — mesmo
                            tratamento opaco usado nos outros mount points. */}
                        <div
                            className='konfidency-reviews-summary hidden overflow-visible leading-none lg:flex lg:items-center [&_*]:!my-0 [&_*]:!py-0 [&_*]:!leading-none'
                            data-sku={productGroupId}
                            suppressHydrationWarning
                            dangerouslySetInnerHTML={{ __html: '' }}
                        />
                        <h2 className='line-clamp-2 shrink-0 text-xs font-bold uppercase leading-4 text-black lg:text-lg lg:leading-[23px]'>
                            {productName}
                        </h2>

                        <div className='mt-auto lg:hidden'>
                            {discountLabel && (
                                <span className='mb-0.5 block w-fit rounded bg-orange px-2 py-0.5 text-xs font-bold text-white'>
                                    {discountLabel}
                                </span>
                            )}
                            {listPrice && (
                                <p className='text-xs leading-[15px] text-neutral-400 line-through'>
                                    De: {listPrice}
                                </p>
                            )}
                            <p className='flex items-baseline gap-1 leading-none text-red-wine'>
                                <strong className='text-lg font-black'>{finalPrice}</strong>
                                {paymentMethod && <span className='text-xs'>{paymentMethod}</span>}
                            </p>
                            {(price || installments) && (
                                <p className='line-clamp-1 text-xs leading-[14px] text-neutral-600'>
                                    {price && <strong>{price}</strong>} {price && 'a prazo'}{' '}
                                    {installments && `ou ${installments}`}
                                </p>
                            )}
                        </div>

                        <Actions
                            mobile
                            hasVariants={hasVariants}
                            disabled={disabled}
                            product={product}
                            quantity={quantity}
                            draft={draft}
                            inventoryLevel={inventoryLevel}
                            analytics={analytics}
                            decrement={decrement}
                            increment={increment}
                            typeQuantity={typeQuantity}
                            changeQuantity={changeQuantity}
                            icon={icon}
                            iconWidth={iconWidth}
                            iconHeight={iconHeight}
                        />
                    </div>
                </div>

                <div className='hidden min-w-0 flex-1 lg:block'>
                    {listPrice && (
                        <p className='text-xs leading-4 text-neutral-400 line-through'>
                            De: {listPrice}
                        </p>
                    )}
                    <div className='flex flex-wrap items-center gap-x-2 gap-y-1'>
                        <p className='flex shrink-0 items-baseline whitespace-nowrap text-red-wine'>
                            <strong className='text-[22px] leading-7'>{finalPrice}</strong>
                            {paymentMethod && <span className='ml-1 text-lg'>{paymentMethod}</span>}
                        </p>
                        {discountLabel && (
                            <span className='shrink-0 whitespace-nowrap rounded bg-orange px-2 py-1 text-base font-bold text-white'>
                                {discountLabel}
                            </span>
                        )}
                    </div>
                    {(price || installments) && (
                        <p className='text-sm leading-6 text-neutral-600'>
                            {price && <strong className='text-base'>{price}</strong>}
                            {price && ' a prazo'}
                            {installments && ` ou ${installments}`}
                        </p>
                    )}
                </div>

                <Actions
                    hasVariants={hasVariants}
                    disabled={disabled}
                    product={product}
                    quantity={quantity}
                    draft={draft}
                    inventoryLevel={inventoryLevel}
                    analytics={analytics}
                    decrement={decrement}
                    increment={increment}
                    typeQuantity={typeQuantity}
                    changeQuantity={changeQuantity}
                    icon={icon}
                    iconWidth={iconWidth}
                    iconHeight={iconHeight}
                />
            </div>
        </aside>
    )
}

interface ActionsProps {
    mobile?: boolean
    hasVariants: boolean
    disabled?: boolean
    product: Item
    quantity: number
    draft: string | null
    inventoryLevel: number
    analytics?: ToAddToCart
    decrement: () => void
    increment: () => void
    typeQuantity: (event: React.ChangeEvent<HTMLInputElement>) => void
    changeQuantity: (event: React.FocusEvent<HTMLInputElement>) => void
    icon?: ImageWidget
    iconWidth?: number
    iconHeight?: number
}

function Actions({
    mobile,
    hasVariants,
    disabled,
    product,
    quantity,
    draft,
    inventoryLevel,
    analytics,
    decrement,
    increment,
    typeQuantity,
    changeQuantity,
    icon,
    iconWidth = 32,
    iconHeight = 32,
}: ActionsProps) {
    const responsiveClass = mobile ? 'flex lg:hidden' : 'hidden lg:flex'
    const size = mobile ? 18 : iconWidth
    const height = mobile ? 18 : iconHeight

    if (hasVariants) {
        return (
            <a
                href='#product-variations'
                className={`${responsiveClass} h-[42px] w-full shrink-0 items-center justify-center gap-1 rounded bg-red-wine px-2 text-sm font-bold text-white lg:h-14 lg:w-[336px] lg:gap-2 lg:text-base lg:hover:bg-[#361318]`}
            >
                {icon && (
                    <Image
                        src={icon}
                        alt=''
                        width={size}
                        height={height}
                        loading='lazy'
                        className='h-[18px] w-[18px] shrink-0 object-contain lg:h-8 lg:w-8'
                    />
                )}
                <span className='whitespace-nowrap lg:hidden'>Selecionar variações</span>
                <span className='hidden whitespace-nowrap lg:inline'>
                    Selecionar variações do produto
                </span>
            </a>
        )
    }

    return (
        <div
            className={`${responsiveClass} h-[42px] w-full shrink-0 items-center gap-1 lg:h-14 lg:w-[336px]`}
        >
            <div className='flex h-full w-[102px] shrink-0 items-center rounded border border-[#cccccc]'>
                <button
                    type='button'
                    className='h-full w-[30%] text-xl font-bold text-neutral-500'
                    onClick={decrement}
                    disabled={quantity <= 1}
                    aria-label='Diminuir quantidade'
                >
                    -
                </button>
                <input
                    className='h-full w-[40%] bg-transparent text-center text-base text-neutral-800 [appearance:textfield] [&::-webkit-inner-spin-button]:m-0'
                    type='number'
                    inputMode='numeric'
                    min={1}
                    max={inventoryLevel}
                    value={draft ?? quantity}
                    onChange={typeQuantity}
                    onBlur={changeQuantity}
                    aria-label='Quantidade'
                />
                <button
                    type='button'
                    className='h-full w-[30%] text-xl font-bold text-neutral-500'
                    onClick={increment}
                    disabled={quantity >= inventoryLevel}
                    aria-label='Aumentar quantidade'
                >
                    +
                </button>
            </div>
            <AddToCartButton
                className='inline-flex h-full min-h-0 min-w-0 flex-1 shrink items-center justify-center gap-1 overflow-visible border-0 bg-red-wine px-1 text-sm font-bold text-white rounded lg:gap-2 lg:px-2 lg:text-base lg:hover:bg-[#361318] focus:!bg-salmon-600 [&_img]:h-[18px] [&_img]:w-[18px] [&_img]:shrink-0 [&_img]:object-contain lg:[&_img]:h-8 lg:[&_img]:w-8 [&_span]:whitespace-nowrap'
                products={[{ ...product, quantity }]}
                icon={icon}
                iconWidth={size}
                iconHeight={height}
                analytics={analytics && {
                    ...analytics,
                    items: analytics.items.map((item) => ({
                        ...item,
                        extended: {
                            ...item.extended,
                            quantity,
                        },
                    })),
                }}
                disabled={disabled}
            />
        </div>
    )
}
